import React from 'react';
import {
  Box,
  ButtonBase,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCompanyAiUsageMonth } from '../hooks/useCompanyAiUsageMonth';
import { formatUsd } from '../lib/aiUsageSpend';

/**
 * Always-visible AppBar spend chip for company_admin / super_admin.
 * Hidden below md — mobile admins reach the breakdown via Sidebar.
 * @param {{
 *   userData?: { companyId?: string, role?: string } | null,
 *   onOpen?: () => void,
 * }} props
 */
export default function AiSpendNavIndicator({ userData, onOpen }) {
  const {
    allowed,
    spendUsd,
    budgetUsd,
    pricingMissingCount,
    loading,
  } = useCompanyAiUsageMonth(userData);

  if (!allowed) return null;

  const hasBudget = typeof budgetUsd === 'number' && budgetUsd > 0;
  const pct = hasBudget
    ? Math.min(100, Math.round((spendUsd / budgetUsd) * 100))
    : 0;
  const remainingUsd = hasBudget
    ? Math.max(0, (Number(budgetUsd) || 0) - (Number(spendUsd) || 0))
    : 0;
  const label = hasBudget
    ? `AI spend: ${formatUsd(spendUsd)} / ${formatUsd(
        budgetUsd,
      )} this month (${formatUsd(remainingUsd)} remaining)`
    : `AI spend: ${formatUsd(spendUsd)} this month`;

  const unpricedNote =
    pricingMissingCount > 0
      ? `${pricingMissingCount} call${
          pricingMissingCount === 1 ? '' : 's'
        } unpriced — total may be higher.`
      : '';

  const content = (
    <ButtonBase
      onClick={onOpen}
      disabled={!onOpen}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 0.5,
        px: 1.25,
        py: 0.75,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        bgcolor: (t) =>
          t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'grey.50',
        textAlign: 'left',
        minWidth: { xs: 140, sm: 200, md: 220 },
        maxWidth: { xs: 280, md: 320, lg: 380 },
        opacity: loading ? 0.7 : 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          lineHeight: 1.3,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        }}
      >
        {loading ? 'AI spend: …' : label}
      </Typography>
      {hasBudget ? (
        <LinearProgress
          variant="determinate"
          value={loading ? 0 : pct}
          color={pct >= 100 ? 'error' : pct >= 80 ? 'warning' : 'primary'}
          sx={{ height: 4, borderRadius: 1, width: '100%' }}
        />
      ) : null}
      {unpricedNote ? (
        <Typography
          variant="caption"
          color="warning.main"
          sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}
          noWrap
        >
          {unpricedNote}
        </Typography>
      ) : null}
    </ButtonBase>
  );

  if (!unpricedNote) {
    return (
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>{content}</Box>
    );
  }

  return (
    <Tooltip title={unpricedNote}>
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>{content}</Box>
    </Tooltip>
  );
}
